
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

// Dominio oficial de producción para la extensión PACS
export const OFFICIAL_APP_URL = 'https://modular.imagenxradiology.com';

// Clave pública RSA fija para asegurar un ID de extensión de Chrome determinista e invariable
// ID resultante de Chrome: pfkbdhdbjillcleegeipcfbcacmolinc
export const EXTENSION_PUBLIC_KEY = 'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA69H7m+LGtIaTohHtAVdrYI/2gQx0OWvRrIQV1YV2D2u3NVnw53PjNiB/UCCy0Df9WurJf4v78KrFsXm4o0J/PEN+Y0qdD9I8GYtzLFDAWYFJc1PTfsFd7JMyAOzLO3UziEqd8bmY59EXr2WlaFJs11VrYzvZQnf35TeMXPXSMEw/GmCjvKT7k5rL9ZI7/DzzHNviT60gNGA8N/6ud1IiybWjwUuy/gIH/vfJ7loVeLTnD4k24SjnaswpOlJj3qmFM8t0EQwZvTVHyhiVvNwWTXy0+Ze/AMVlc2n4zJifBt3EX7tmg2gQUCOKiZzOxdgUZ+AyxabbR6BmgQGjD06QgwIDAQAB';

export const EXTENSION_ID = 'pfkbdhdbjillcleegeipcfbcacmolinc';

export const generateExtensionZip = async (appUrl?: string) => {
  const targetAppUrl = OFFICIAL_APP_URL || appUrl;
  const zip = new JSZip();

  // Función para generar el icono de Modular
  const generateIconBlob = (size: number): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return resolve(new Blob());

      const p = size * 0.08; // padding
      const s = (size - p * 3) / 2; // size of each square
      const r = size * 0.1; // radius

      const drawRect = (x: number, y: number, color: string, alpha: number = 1) => {
        ctx.globalAlpha = alpha;
        ctx.fillStyle = color;
        // Fallback para roundRect si no existe
        if (ctx.roundRect) {
          ctx.beginPath();
          ctx.roundRect(x, y, s, s, r);
          ctx.fill();
        } else {
          ctx.fillRect(x, y, s, s);
        }
      };

      drawRect(p, p, '#2563eb'); // Blue-600
      drawRect(p * 2 + s, p, '#2563eb', 0.1); // Blue-50 equivalent
      drawRect(p, p * 2 + s, '#4f46e5', 0.7); // Indigo-600 equivalent
      drawRect(p * 2 + s, p * 2 + s, '#a855f7', 0.9); // Purple-500 equivalent

      canvas.toBlob((blob) => resolve(blob || new Blob()), 'image/png');
    });
  };

  const icon16 = await generateIconBlob(16);
  const icon48 = await generateIconBlob(48);
  const icon128 = await generateIconBlob(128);

  const manifest = {
    manifest_version: 3,
    name: "Modular",
    version: "1.1.0",
    key: EXTENSION_PUBLIC_KEY,
    description: "Asistente de IA para Radiología. Integración con sistemas PACS.",
    icons: {
      "16": "icon16.png",
      "48": "icon48.png",
      "128": "icon128.png"
    },
    permissions: [
      "activeTab",
      "scripting",
      "storage",
      "sidePanel"
    ],
    host_permissions: [
      "*://*/*"
    ],
    background: {
      service_worker: "background.js"
    },
    action: {
      default_title: "Abrir Modular",
      default_icon: {
        "16": "icon16.png",
        "48": "icon48.png",
        "128": "icon128.png"
      }
    },
    side_panel: {
      "default_path": "sidepanel.html"
    },
    content_scripts: [
      {
        "matches": ["<all_urls>"],
        "js": ["content.js"],
        "run_at": "document_idle"
      }
    ]
  };

  const backgroundJs = `
// Configurar comportamiento del panel lateral
chrome.sidePanel
  .setPanelBehavior({ openPanelOnActionClick: true })
  .catch((error) => console.error(error));

// Escuchar mensajes internos
chrome.runtime.onMessage.addListener((request, sender, sendResponse) => {
  if (request.type === 'MODULAR_HANDSHAKE') {
    sendResponse({ status: 'OK', version: '1.1.0' });
    return true;
  }
  
  if (request.type === 'MODULAR_PASTE_REQUEST') {
    chrome.tabs.query({ active: true, currentWindow: true }, (tabs) => {
      const activeTab = tabs[0];
      if (activeTab) {
        chrome.tabs.sendMessage(activeTab.id, {
          type: 'EXT_PERFORM_PASTE',
          text: request.text
        });
      }
    });
  }

  if (request.type === 'OPEN_PERMISSIONS_TAB') {
    chrome.tabs.create({ url: request.url });
  }

  return true;
});
`;

  const contentJs = `
// Escuchar mensajes desde la página de MODULAR
window.addEventListener('message', (event) => {
  if (event.data?.type === 'MODULAR_HANDSHAKE_REQUEST') {
    chrome.runtime.sendMessage({ type: 'MODULAR_HANDSHAKE' }, (response) => {
      if (response && response.status === 'OK') {
        window.postMessage({ type: 'MODULAR_HANDSHAKE_RESPONSE' }, '*');
      }
    });
  }
  if (event.data?.type === 'MODULAR_PASTE_TO_EXT') {
    chrome.runtime.sendMessage({ type: 'MODULAR_PASTE_REQUEST', text: event.data.text });
  }
  if (event.data?.type === 'MODULAR_OPEN_PERMISSIONS') {
    chrome.runtime.sendMessage({ type: 'OPEN_PERMISSIONS_TAB', url: event.data.url });
  }
});

// Escuchar mensajes desde el background script
chrome.runtime.onMessage.addListener((request, sender, sendResponse) => {
  if (request.type === 'EXT_PERFORM_PASTE') {
    const activeElement = document.activeElement;
    
    const performPaste = (el) => {
      if (el.isContentEditable) {
        el.innerText = request.text;
      } else {
        el.value = request.text;
      }
      el.dispatchEvent(new Event('input', { bubbles: true }));
      el.dispatchEvent(new Event('change', { bubbles: true }));
    };

    if (activeElement && (activeElement.tagName === 'TEXTAREA' || (activeElement.tagName === 'INPUT' && activeElement.type === 'text') || activeElement.isContentEditable)) {
      performPaste(activeElement);
    } else {
      const commonSelectors = ['textarea', 'input[type="text"]', '[contenteditable="true"]', '.pacs-editor', '#report-text'];
      for (const selector of commonSelectors) {
        const el = document.querySelector(selector);
        if (el) {
          performPaste(el);
          return;
        }
      }
    }
  }
});
`;

  const sidepanelHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Modular</title>
  <style>
    body, html { margin: 0; padding: 0; height: 100%; width: 100%; overflow: hidden; background: #1e2024; font-family: sans-serif; }
    iframe { border: none; width: 100%; height: 100%; }
    #perm-banner { 
      display: none; 
      background: #2563eb; 
      color: white; 
      padding: 8px; 
      font-size: 12px; 
      text-align: center; 
      cursor: pointer; 
      font-weight: bold;
    }
  </style>
</head>
<body>
  <div id="perm-banner">⚠️ Haz clic aquí para activar el micrófono</div>
  <iframe id="modular-frame" src="${targetAppUrl}?layout=single&view=MODULAR_1&theme=dark" allow="microphone; camera; display-capture; autoplay; clipboard-read; clipboard-write"></iframe>
  <script src="sidepanel.js"></script>
</body>
</html>
`;

  const sidepanelJs = `
const frame = document.getElementById('modular-frame');
const banner = document.getElementById('perm-banner');
const appUrl = "${targetAppUrl}";

// Verificar permisos de micrófono
navigator.mediaDevices.getUserMedia({ audio: true })
  .then((stream) => {
    stream.getTracks().forEach((t) => t.stop());
    if (banner) banner.style.display = 'none';
  })
  .catch((err) => {
    console.warn("Micrófono bloqueado en sidepanel:", err);
    if (banner) banner.style.display = 'block';
  });

if (banner) {
  banner.onclick = () => {
    chrome.tabs.create({ url: appUrl });
  };
}

window.addEventListener('message', (event) => {
  if (event.data?.type === 'MODULAR_MIC_ERROR') {
    if (banner) banner.style.display = 'block';
  }
});
`;

  zip.file("manifest.json", JSON.stringify(manifest, null, 2));
  zip.file("background.js", backgroundJs);
  zip.file("content.js", contentJs);
  zip.file("sidepanel.html", sidepanelHtml);
  zip.file("sidepanel.js", sidepanelJs);
  zip.file("icon16.png", icon16);
  zip.file("icon48.png", icon48);
  zip.file("icon128.png", icon128);

  const content = await zip.generateAsync({ type: "blob" });
  saveAs(content, "modular-extension.zip");
};
